import {memo} from "react";
import {Icon, IconButton} from "@chakra-ui/react";
import {EllipsisVerticalIcon} from "@heroicons/react/16/solid";
import {
  ArrowDownTrayIcon,
  Bars3BottomLeftIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import {MenuContent, MenuItem, MenuRoot, MenuSeparator, MenuTrigger} from "../../../../../components/ui/menu";

type RowActionsMenuProps = {
  /** Trigger styling — "ghost" for inline table rows, "outline" for the white action bars on detail views. */
  variant?: "ghost" | "outline";
  /** Invoked when "Update description" is chosen (e.g. opens the edit dialog on a detail view). */
  onEditDescription?: () => void;
  /** Show a "Download" item — used by the detail views, where download lives in this menu. */
  download?: boolean;
};

/**
 * "..." actions menu — Update title / Update description / Clone / (Download) / Delete. Shared by
 * the list tables and the detail views. No-op prototype; only "Update description" is wired up
 * (via `onEditDescription`). `download` adds the export item for the detail views.
 */
export const RowActionsMenu = memo(({variant = "ghost", onEditDescription, download}: RowActionsMenuProps) => (
  <MenuRoot>
    <MenuTrigger asChild>
      <IconButton
        size="xs"
        variant={variant}
        colorPalette="gray"
        bg={variant === "outline" ? "white" : undefined}
        aria-label="More actions"
      >
        <Icon asChild>
          <EllipsisVerticalIcon />
        </Icon>
      </IconButton>
    </MenuTrigger>
    <MenuContent>
      <MenuItem value="update-title">
        <Icon asChild>
          <PencilSquareIcon />
        </Icon>
        Update title
      </MenuItem>
      <MenuItem value="edit-description" onClick={onEditDescription}>
        <Icon asChild>
          <Bars3BottomLeftIcon />
        </Icon>
        Update description
      </MenuItem>
      <MenuItem value="clone">
        <Icon asChild>
          <DocumentDuplicateIcon />
        </Icon>
        Clone
      </MenuItem>
      {download && (
        <>
          <MenuSeparator />
          <MenuItem value="download">
            <Icon asChild>
              <ArrowDownTrayIcon />
            </Icon>
            Download
          </MenuItem>
          <MenuSeparator />
        </>
      )}
      <MenuItem value="delete" color="fg.error" _hover={{bg: "bg.error", color: "fg.error"}}>
        <Icon asChild>
          <TrashIcon />
        </Icon>
        Delete
      </MenuItem>
    </MenuContent>
  </MenuRoot>
));
